import { useState } from 'react'
import { useMistakes, useWrongAnswers, type AdminMistake } from '@web/lib/admin'
import { ExerciseEditor } from './ExerciseEditor'

/**
 * What she gets wrong, worst first.
 *
 * The point of this screen is the *actual answers she typed*, not the score. A
 * native speaker reading "she wrote 'eu sou trinta anos'" immediately knows
 * whether the content is wrong, the wording is ambiguous, or she has a real
 * gap — and those three need completely different fixes.
 */
export function MistakesView({ userId }: { userId: string }) {
  const { data, isPending } = useMistakes(userId)
  const [openId, setOpenId] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)

  if (isPending) return <p className="text-ink-500">Loading…</p>

  const mistakes = data?.mistakes ?? []
  if (mistakes.length === 0) {
    return (
      <p className="rounded-card bg-white p-6 text-ink-700 ring-1 ring-brand-900/5">
        Nothing wrong yet — either she is doing well or there is not enough data.
      </p>
    )
  }

  return (
    <>
      <p className="mb-3 text-sm text-ink-500">
        {mistakes.length} exercises missed on the first try, hardest first.
      </p>
      <ul className="space-y-2">
        {mistakes.map((m) => (
          <MistakeRow
            key={m.exerciseId}
            mistake={m}
            userId={userId}
            open={openId === m.exerciseId}
            onToggle={() => setOpenId(openId === m.exerciseId ? null : m.exerciseId)}
            onEdit={() => setEditingId(m.exerciseId)}
          />
        ))}
      </ul>

      {editingId && (
        <ExerciseEditor exerciseId={editingId} onClose={() => setEditingId(null)} />
      )}
    </>
  )
}

function MistakeRow({
  mistake,
  userId,
  open,
  onToggle,
  onEdit,
}: {
  mistake: AdminMistake
  userId: string
  open: boolean
  onToggle: () => void
  onEdit: () => void
}) {
  const { data } = useWrongAnswers(userId, open ? mistake.exerciseId : null)

  return (
    <li className="rounded-card bg-white ring-1 ring-brand-900/5">
      <div className="flex items-start gap-3 p-4">
        <button type="button" onClick={onToggle} aria-expanded={open} className="flex-1 text-left">
          <p className="font-medium text-ink-900">{mistake.prompt}</p>
          <p className="mt-0.5 text-xs text-ink-500">
            {mistake.unitTitle} · {mistake.lessonTitle} · {mistake.type}
            {mistake.skillLabel && <> · {mistake.skillLabel}</>}
          </p>
        </button>
        <div className="flex shrink-0 items-center gap-3">
          <span
            className={`rounded-full px-2.5 py-1 text-xs font-semibold tabular-nums ${
              mistake.accuracyPct === 0
                ? 'bg-clay-50 text-clay-700'
                : 'bg-gold-100 text-gold-700'
            }`}
          >
            {mistake.accuracyPct}%
          </span>
          <button
            type="button"
            onClick={onEdit}
            className="text-sm font-semibold text-brand-600 hover:underline"
          >
            Edit
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-sand-200 px-4 py-3">
          <p className="text-xs font-semibold tracking-wide text-ink-400 uppercase">
            What she answered
          </p>
          {!data ? (
            <p className="mt-2 text-sm text-ink-500">Loading…</p>
          ) : data.answers.length === 0 ? (
            <p className="mt-2 text-sm text-ink-500">No stored answers.</p>
          ) : (
            <ul className="mt-2 space-y-1.5">
              {data.answers.map((a, i) => (
                <li key={i} className="rounded-lg bg-clay-50 px-3 py-2 text-sm">
                  <code className="text-clay-700" lang="pt-BR">
                    {formatAnswer(a.given)}
                  </code>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </li>
  )
}

/**
 * Renders a stored response as the thing she actually chose or typed.
 *
 * Responses are the raw JSON the grader received, whose shape differs per
 * exercise type — dumping it verbatim would make the most useful column on
 * this screen unreadable.
 */
function formatAnswer(given: unknown): string {
  if (typeof given !== 'object' || given === null) return String(given)
  const r = given as Record<string, unknown>

  if (typeof r.text === 'string') return r.text
  if (typeof r.optionId === 'string') return `option ${r.optionId}`
  if (Array.isArray(r.values)) return r.values.join(' / ')
  if (Array.isArray(r.order)) return `order ${r.order.join(', ')}`
  if (Array.isArray(r.pairs)) {
    return r.pairs
      .map((p) => {
        const pair = p as { leftIndex?: number; rightIndex?: number }
        return `${pair.leftIndex}→${pair.rightIndex}`
      })
      .join('  ')
  }
  if (typeof r.transcript === 'string') return r.transcript
  return JSON.stringify(given)
}
